import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { X, Sparkles } from "lucide-react";

export default function ExplainDrawer({ open, onClose, kind, itemId, title }) {
  const { data: audit, isLoading, error } = useQuery({
    queryKey: ["explain", kind, itemId],
    queryFn: () => api.get(`/ranking/explain/${kind}/${itemId}`).then((r) => r.data),
    enabled: !!open && !!itemId,
    retry: false,
  });
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-end">
      <div className="w-full max-w-md h-full bg-[#0A0A0A] border-l border-[#27272A] overflow-y-auto">
        <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300 inline-flex items-center gap-2">
            <Sparkles size={11} /> WHY_THIS_RANK
          </span>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-explain">
            <X size={14} />
          </button>
        </div>
        <div className="p-3 space-y-3 text-[12px]">
          {title && <div className="text-white">{title}</div>}
          {isLoading && (
            <div className="text-zinc-500 font-mono text-[11px]">LOADING_AUDIT…</div>
          )}
          {error && (
            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-100 font-mono p-2 text-[11px]">
              No audit recorded yet. Ask an admin/owner to RE-RANK from /admin/ranking.
            </div>
          )}
          {audit && (
            <>
              <div className="border border-[#27272A] p-3 bg-[#0E0E0E]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">FINAL_SCORE</div>
                <div className="text-3xl font-mono tabnum text-emerald-300 mt-1">
                  {Number(audit.final_score).toFixed(2)}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono">
                  weights v{audit.weights_version} · {audit.computed_at?.slice(0, 19).replace("T", " ")}
                </div>
                <div className="text-[12px] text-zinc-300 mt-2">{audit.reason}</div>
              </div>

              <ComponentBlock title="IDEA_COMPONENTS" data={audit.components?.idea} accent="text-purple-300" />
              <ComponentBlock title="SOURCE_COMPONENTS" data={audit.components?.source} accent="text-amber-300" />

              <div className="border border-[#27272A]">
                <div className="px-3 py-1 border-b border-[#27272A] text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">
                  RAW_INPUTS
                </div>
                <div className="p-2 grid grid-cols-2 gap-1 text-[11px] font-mono">
                  {Object.entries(audit.inputs || {}).map(([k, v]) => (
                    <div key={k} className="flex">
                      <span className="text-zinc-500 w-32 uppercase">{k}</span>
                      <span className="ml-auto tabnum">{Number(v).toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-[#27272A]">
                <div className="px-3 py-1 border-b border-[#27272A] text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">
                  WEIGHTS_USED
                </div>
                <div className="p-2 grid grid-cols-2 gap-1 text-[11px] font-mono">
                  {Object.entries(audit.weights_snapshot || {}).filter(([k]) => k !== "id" && k !== "version").map(([k, v]) => (
                    <div key={k} className="flex">
                      <span className="text-zinc-500 w-32 uppercase">{k}</span>
                      <span className="ml-auto tabnum text-amber-300">{Number(v).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ComponentBlock({ title, data, accent }) {
  if (!data) return null;
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map(([, v]) => Number(v))) || 1;
  return (
    <div className="border border-[#27272A]">
      <div className={"px-3 py-1 border-b border-[#27272A] text-[10px] uppercase tracking-[0.2em] font-mono " + accent}>
        {title}
      </div>
      <div className="p-2 space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="text-[11px] font-mono">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 uppercase">{k.replace(/_/g, " ")}</span>
              <span className="tabnum">{Number(v).toFixed(2)}</span>
            </div>
            <div className="mt-1 h-[3px] bg-[#1a1a1a]">
              <div className="bg-amber-400" style={{ width: `${(Number(v) / max) * 100}%`, height: "100%" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
